import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import Session from 'users/session/session';
import Guid from 'common/values/guid/guid';
import CompanyAPIService from 'marketplace/entities/company/api/company-api-service';
import IndividualAPIService from 'marketplace/entities/individual/api/individual-api-service';
import MarketplaceTeamAPIService from 'marketplace/entities/marketplace-team/api/marketplace-team-api-service';
import React, { useEffect, useMemo } from 'react';

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  height: theme.spacing(10),
  width: theme.spacing(10)
}));
const LoadingProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 700],
  height: '75%',
  width: '75%'
}));

export enum AvatarType {
  Individual,
  Company,
  Team
}

export interface AvatarUploadProps {
  className?: string;
  accept: string;
  id?: Guid;
  type: AvatarType;
  avatar?: File | string | null;
  session: Session;
  onUpload: (updatedAvatar: File) => void;
}

export default function AvatarUpload(props: Readonly<AvatarUploadProps>) {
  const {
    className,
    accept,
    id,
    type,
    avatar,
    session,
    onUpload
  } = props;

  const hiddenAvatarFileInput = React.useRef<HTMLInputElement>(null);

  const [uploadingAvatar, setUploadingAvatar] = React.useState<boolean>(false);
  const [imageLoading, setImageLoading] = React.useState<boolean>(true);

  const imageSrc = useMemo(() => {
    if (avatar instanceof File) return URL.createObjectURL(avatar);
    return avatar;
  }, [avatar]);

  // Stop showing loading indicator after 5 seconds
  useEffect(() => {
    if (imageSrc === null) setImageLoading(false);
    const timeout = setTimeout(() => {
      setImageLoading(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [imageSrc]);

  async function handleAvatarChange(event: React.ChangeEvent<HTMLInputElement>) {
    setUploadingAvatar(true);

    const files = event.target.files;

    if (!files || files.length === 0) return;

    const avatarFile = files[0];

    if (!id) {
      onUpload(avatarFile);
      setUploadingAvatar(false);
      return;
    }

    try {
      if (type === AvatarType.Company) {
        const companyService = new CompanyAPIService(session);
        await companyService.createCompanyAvatar(id, avatarFile);
      }
      if (type === AvatarType.Individual) {
        const individualService = new IndividualAPIService(session);
        await individualService.createIndividualAvatar(id, avatarFile);
      }
      if (type === AvatarType.Team) {
        const teamService = new MarketplaceTeamAPIService(session);
        await teamService.createTeamAvatar(id, avatarFile);
      }
      onUpload(avatarFile);
    } catch (error) {
      console.error(error);
    } finally {
      setUploadingAvatar(false);
      hiddenAvatarFileInput.current!.value = '';
    }
  }

  return (
    <div className={className}>
      <input
        type="file"
        ref={hiddenAvatarFileInput}
        accept={accept}
        onChange={handleAvatarChange}
        style={{ display: 'none' }}
      />
      <Badge
        overlap="rectangular"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        badgeContent={
          <Tooltip title="Upload avatar">
            <span>
              <IconButton
                color="primary"
                aria-label="upload avatar"
                component="span"
                onClick={() => hiddenAvatarFileInput.current?.click()}
                size="medium">
                {uploadingAvatar 
                  ? <CircularProgress size="24px" disableShrink /> 
                  : <AddAPhotoIcon />
                }
              </IconButton>
            </span>
          </Tooltip>
        }>
        <StyledAvatar
          src={imageSrc ?? undefined}
          slotProps={{
            img: {
              onLoad: () => setImageLoading(false),
              onError: () => setImageLoading(false)
            }
          }}
        >
          {imageLoading && <LoadingProgress disableShrink />}
        </StyledAvatar>
      </Badge>
    </div>
  );
}
